import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';

const VenueManagementLayer = () => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limits, setLimits] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, venue: null });

    // Fetch venues and limits
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [venuesRes, limitsRes] = await Promise.all([
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/my-venues`),
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/limits`)
            ]);

            if (venuesRes.ok) {
                const venuesData = await venuesRes.json();
                // API returns { venues, subscription, usage } - extract venues array
                setVenues(Array.isArray(venuesData) ? venuesData : (venuesData.venues || []));
            }

            if (limitsRes.ok) {
                const limitsData = await limitsRes.json();
                setLimits(limitsData);
            }
        } catch (error) {
            console.error('Error fetching venues:', error);
            toast.error('Failed to load venues');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = async () => {
        if (!deleteModal.venue) return;

        try {
            const response = await auth.fetch(
                `${config.API_BASE_URL}/api/v2/venues/${deleteModal.venue.id}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                toast.success('Venue deleted successfully');
                setDeleteModal({ show: false, venue: null });
                fetchData();
            } else {
                const error = await response.json();
                toast.error(error.message || 'Failed to delete venue');
            }
        } catch (error) {
            console.error('Error deleting venue:', error);
            toast.error('Failed to delete venue');
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { bg: 'bg-warning-focus', text: 'text-warning-main', label: 'Pending' },
            approved: { bg: 'bg-success-focus', text: 'text-success-main', label: 'Approved' },
            rejected: { bg: 'bg-danger-focus', text: 'text-danger-main', label: 'Rejected' },
            suspended: { bg: 'bg-secondary-focus', text: 'text-secondary-main', label: 'Suspended' }
        };
        const s = statusMap[status] || statusMap.pending;
        return (
            <span className={`${s.bg} ${s.text} px-16 py-6 radius-4 fw-medium text-sm`}>
                {s.label}
            </span>
        );
    };

    const canAddVenue = limits?.limits?.venues 
        ? limits.limits.venues.current < limits.limits.venues.max || limits.limits.venues.max === -1
        : true;

    const getLimitProgress = () => {
        if (!limits?.limits?.venues) return null;
        const { current, max } = limits.limits.venues;
        if (max === -1) return null; // Unlimited
        const percentage = Math.min((current / max) * 100, 100);
        const color = percentage >= 90 ? 'bg-danger-main' : percentage >= 70 ? 'bg-warning-main' : 'bg-success-main';
        return { current, max, percentage, color };
    };

    const limitProgress = getLimitProgress();
    
    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    if (loading) {
        return (
            <div className="card h-100 p-0 radius-12">
                <div className="card-body p-24 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Limit Progress Card */}
            {limitProgress && (
                <div className="card mb-24">
                    <div className="card-body p-24">
                        <div className="d-flex justify-content-between align-items-center mb-12">
                            <h6 className="fw-semibold mb-0">
                                <Icon icon="mdi:store" className="me-8 text-primary" />
                                Venue Limit
                            </h6>
                            <span className="text-secondary-light">
                                {limitProgress.current} / {limitProgress.max} venues
                            </span>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                            <div 
                                className={`progress-bar ${limitProgress.color}`} 
                                role="progressbar" 
                                style={{ width: `${limitProgress.percentage}%` }}
                                aria-valuenow={limitProgress.percentage} 
                                aria-valuemin="0" 
                                aria-valuemax="100"
                            />
                        </div>
                        {limitProgress.percentage >= 90 && (
                            <p className="text-danger-main text-sm mt-8 mb-0 d-flex align-items-center">
                                <Icon icon="mdi:alert" className="me-8 text-lg" />
                                <span>
                                    You're almost at your venue limit. 
                                    <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent text-primary-600 fw-medium ms-4">Upgrade your plan</button>
                                </span>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Venues Table */}
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <h6 className="fw-semibold mb-0">My Venues</h6>
                        <span className="badge bg-primary-focus text-primary-main px-12 py-6 radius-4">
                            {venues.length} {venues.length === 1 ? 'venue' : 'venues'}
                        </span>
                    </div>
                    <button
                        className="btn btn-primary-600 text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        onClick={() => navigate('/add-venue')}
                        disabled={!canAddVenue}
                        title={!canAddVenue ? 'Venue limit reached. Upgrade your plan.' : ''}
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add Venue
                    </button>
                </div>

                <div className="card-body p-24">
                    {venues.length === 0 ? (
                        <div className="text-center py-40 d-flex flex-column align-items-center justify-content-center">
                            <Icon icon="mdi:store-off" className="text-secondary-light mb-16" style={{ fontSize: '64px' }} />
                            <h5 className="text-secondary-light mb-8">No Venues Yet</h5>
                            <p className="text-secondary-light mb-24">
                                Create your first venue to start managing your business.
                            </p>
                            <button
                                className="btn btn-primary-600 d-flex align-items-center gap-2"
                                onClick={() => navigate('/add-venue')}
                                disabled={!canAddVenue}
                            >
                                <Icon icon="ic:baseline-plus" className="text-xl line-height-1" />
                                Create Your First Venue
                            </button>
                        </div>
                    ) : (
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Venue</th>
                                        <th scope="col">Location</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Resources</th>
                                        <th scope="col" className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {venues.map((venue) => (
                                        <tr key={venue.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-12">
                                                    {venue.logo_url ? (
                                                        <img 
                                                            src={venue.logo_url} 
                                                            alt={venue.title}
                                                            className="w-48-px h-48-px rounded-circle object-fit-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-48-px h-48-px rounded-circle bg-primary-focus d-flex align-items-center justify-content-center">
                                                            <Icon icon="mdi:store" className="text-primary-main text-xl" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h6 className="text-md fw-semibold mb-0">
                                                            <Link to={`/venue/${venue.id}`} className="text-primary-600 hover-text-primary">
                                                                {venue.title || venue.business_name}
                                                            </Link>
                                                        </h6>
                                                        <span className="text-sm text-secondary-light">
                                                            {venue.description?.substring(0, 50)}
                                                            {venue.description?.length > 50 ? '...' : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="text-sm fw-medium">
                                                        {venue.address_city || venue.address?.split(',')[1]?.trim() || '-'}
                                                    </span>
                                                    <span className="text-sm text-secondary-light">
                                                        {venue.address_country || venue.address?.split(',').pop()?.trim() || '-'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                {getStatusBadge(venue.verification_status || 'pending')}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-16">
                                                    <div className="d-flex align-items-center gap-4" title="Gallery Images">
                                                        <Icon icon="mdi:image-multiple" className="text-primary-600" />
                                                        <span className="text-sm">{venue.gallery_count || 0}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-4" title="Features">
                                                        <Icon icon="mdi:star" className="text-warning-main" />
                                                        <span className="text-sm">{venue.feature_count || 0}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-4" title="Blog Posts">
                                                        <Icon icon="mdi:post" className="text-info-main" />
                                                        <span className="text-sm">{venue.blog_count || 0}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-10 justify-content-center">
                                                    <button
                                                        type="button"
                                                        className="bg-info-focus text-info-main w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                                                        onClick={() => navigate(`/venue/${venue.id}`)}
                                                        title="View Details"
                                                    >
                                                        <Icon icon="iconamoon:eye-light" className="text-lg" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="bg-success-focus text-success-main w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                                                        onClick={() => navigate(`/edit-venue/${venue.id}`)}
                                                        title="Edit Venue"
                                                    >
                                                        <Icon icon="lucide:edit" className="text-lg" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="bg-danger-focus text-danger-main w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                                                        onClick={() => setDeleteModal({ show: true, venue })}
                                                        title="Delete Venue"
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" className="text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Delete Venue</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setDeleteModal({ show: false, venue: null })}
                                />
                            </div>
                            <div className="modal-body">
                                <div className="text-center py-16">
                                    <Icon icon="mdi:alert-circle" className="text-danger-main mb-16" style={{ fontSize: '48px' }} />
                                    <p className="mb-0">
                                        Are you sure you want to delete <strong>{deleteModal.venue?.title || deleteModal.venue?.business_name}</strong>?
                                    </p>
                                    <p className="text-secondary-light text-sm mt-8">
                                        This will also delete all gallery images, features, and blog posts associated with this venue.
                                    </p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary-600"
                                    onClick={() => setDeleteModal({ show: false, venue: null })}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger-600"
                                    onClick={handleDelete}
                                >
                                    <Icon icon="fluent:delete-24-regular" className="me-4" />
                                    Delete Venue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VenueManagementLayer;
